/// <amd-module name="Build/Run/Components/SourcesCard" />
import { BuildReason } from "Build/Client/Contracts/Build";
import { ChangesData, RepositoryResourceData, RunDetailsData } from "Build/Client/Contracts/DataProvider";
import { BranchLink } from "Build/Common/Library/Components/BranchLink";
import { CommitLink } from "Build/Common/Library/Components/CommitLink";
import { RepoLink } from "Build/Common/Library/Components/RepoLink";
import * as Resources from "Build/Run/Resources";
import { IRunDetailsService } from "Build/Run/RunDetailsService.types";
import { RepositoryTypes } from "Build/SourceProviders/Common";
import * as SourceProviderResources from "Build/SourceProviders/Resources";
import * as React from "react";
import { IReadonlyObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Observer } from "VSSUI/Observer";
import { ITableColumn, SimpleTableCell, Table, TwoLineTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { WorkItemsLink } from "./WorkItemsLink";

export class SourcesCard extends VssComponent {
    private _runDetailsService: IRunDetailsService;
    private _columns: ITableColumn<RepositoryResourceData>[];

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this._runDetailsService = context.pageContext.getService<IRunDetailsService>("IRunDetailsService");

        this._columns = [
            {
                className: "pipelines-sourcecard-repositorycolumn",
                id: "repository",
                name: Resources.SourceCardRepositoryColumnTitle,
                renderCell: this._renderRepositoryColumn,
                width: -30

            },
            {
                className: "pipelines-sourcecard-versioncolumn",
                id: "Version",
                name: Resources.SourceCardBranchColumnTitle,
                renderCell: this._renderBranchColumn,
                width: -30
            },
            {
                className: "pipelines-sourcecard-changescolumn",
                id: "Changes",
                name: Resources.SourceCardVersionColumnTitle,
                renderCell: this._renderVersionColumn,
                width: -20
            },
            {
                className: "pipelines-sourcecard-relatedcolumn",
                id: "Version",
                name: Resources.SourceCardRelatedColumnTitle,
                renderCell: this._renderRelatedColumn,
                width: -20
            }
        ];
    }

    public render(): JSX.Element {
        return <Observer runData={this._runDetailsService.data}>
            {(props: { runData: RunDetailsData }) => { 
                if (props.runData.repositoryResources && props.runData.repositoryResources.length > 1) {
                return (
                    <div id="SourceCard">
                        <Card titleProps={{text: Resources.SourceCardTitle}} className="sources-card flex-grow bolt-table-card margin-top-16" contentProps={{ contentPadding: false }}>
                            <Table<RepositoryResourceData>
                                ariaLabel={Resources.SourceCardTitle}
                                itemProvider={new ArrayItemProvider(getRepositoryResourceItems(props.runData, this.context))}
                                columns={this._columns}
                            />
                        </Card>
                    </div>
                    );
                } else {
                    return null;
                }
            }}
        </Observer>
    }

    private _renderRepositoryColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RepositoryResourceData>,
        tableItem: RepositoryResourceData
    ): JSX.Element => {
        const repositoryName = tableItem.name;
        const repositoryType = tableItem.type;
        return (
            <TwoLineTableCell
                className="sources-card-table-cell"
                key={"col-" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                line1={
                    <span className="flex-row scroll-hidden sourcecard-repositorylink">
                        <Tooltip text={repositoryName} overflowOnly>
                            <RepoLink 
                                sourceRepositoryId={tableItem.id} 
                                sourceRepositoryName={tableItem.name} 
                                sourceRepositoryType={tableItem.type} 
                                sourceRepositoryUrl={tableItem.url}
                                includeIcon={false} />
                        </Tooltip>
                    </span>
                }
                line2={
                    <Tooltip text={repositoryType} overflowOnly>
                        <span className="fontSize secondary-text sources-card-repotype flex-row flex-center text-ellipsis">
                            <span className="text-ellipsis" key="release-type-text">
                                {friendlyRepositoryType(repositoryType)}
                            </span>
                        </span>
                    </Tooltip>
                }
            />
        );
    }

    private _renderBranchColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RepositoryResourceData>,
        tableItem: RepositoryResourceData
    ): JSX.Element => {

        return (
            <SimpleTableCell
            columnIndex={columnIndex}
            tableColumn={tableColumn}
            key={"col-" + columnIndex}
            contentClassName="sources-card-table-cell fontSizeM scroll-hidden"
        >
            <BranchLink
                excludeTabStop={false}
                repositoryType={tableItem.type}
                repositoryName={tableItem.name}
                repositoryId={tableItem.id}
                repositoryUrl={tableItem.connectionId}
                branch={tableItem.refName}
                monospace={false} />
        </SimpleTableCell>
        );
    }

    private _renderVersionColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RepositoryResourceData>,
        tableItem: RepositoryResourceData
    ): JSX.Element => {
        return (
            <SimpleTableCell
            columnIndex={columnIndex}
            tableColumn={tableColumn}
            key={"col-" + columnIndex}
            contentClassName="sources-card-table-cell fontSizeM scroll-hidden"
        >
            <CommitLink
                excludeTabStop={false}
                repositoryName={tableItem.name}
                repositoryId={tableItem.id}
                repositoryType={tableItem.type}
                commitId={tableItem.version}
                monospace={false}
                showIcon={false} />
        </SimpleTableCell>
        );
    }
    
    private _renderRelatedColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RepositoryResourceData>,
        tableItem: RepositoryResourceData
    ): JSX.Element => {
        return (
            <SimpleTableCell
            columnIndex={columnIndex}
            tableColumn={tableColumn}
            key={"col-" + columnIndex}
            contentClassName="fontSizeM size-m scroll-hidden"
        >
            <RelatedCell repository={tableItem} associatedChanges={this._runDetailsService.associatedChanges} />
        </SimpleTableCell>
        );
    }
}

interface IRelatedCellProps {
    repository: RepositoryResourceData;
    associatedChanges: IReadonlyObservableValue<ChangesData>;
}

class RelatedCell extends React.PureComponent<IRelatedCellProps, {}> {
    public render() {
        // for now, we only support changes for the primary repository
        if (this.props.repository.alias === "self") {
            return <WorkItemsLink 
                associatedChangesData={this.props.associatedChanges} 
                sourceType={this.props.repository.type}
                showIcon={false} />
        }
        else {
            return null;
        }
    }
}

function friendlyRepositoryType(repoistoryType: string): string {
    switch(repoistoryType.toLowerCase()) {
        case RepositoryTypes.TfsGit:
            return SourceProviderResources.TfsGit;
        case RepositoryTypes.Bitbucket:
            return SourceProviderResources.Bitbucket;
        case RepositoryTypes.ExternalGit:
            return SourceProviderResources.ExternalGit;
        case RepositoryTypes.ExternalUnknown:
            return SourceProviderResources.ExternalUnknown;
        case RepositoryTypes.GitHub:
            return SourceProviderResources.GitHub;
        case RepositoryTypes.GitHubEnterprise:
            return SourceProviderResources.GitHubEnterprise;
        case RepositoryTypes.Svn:
            return SourceProviderResources.Svn;
        case RepositoryTypes.TfsVersionControl:
            return SourceProviderResources.TfsVersionControl;
        default:
            return repoistoryType;
    }
}

/**
 * Gets the repositories from the run, removing duplicates and fixing PR link.
 */
export function getRepositoryResourceItems(run: RunDetailsData, context: IVssComponentContext): RepositoryResourceData[] {
    const uniqueResources: {[key: string]: RepositoryResourceData} = {};

    // Let's remove duplications of repositories, giving prevalence to "self"
    for (const item of run.repositoryResources || []) {
        const key = `${item.type}##${item.name}##${item.refName}##${item.version}`;

        if (!uniqueResources[key] || item.alias === "self") {
            const refName = item.alias === "self" &&
                (!isFeatureFlagEnabled(context.pageContext, "Build2.SelectTriggeringRepositoriesBasedOnSHA") ||
                    (run.reason === BuildReason.PullRequest && run.triggerInfo["pr.triggerRepository"] === item.id))
                ? run.sourceBranch
                : item.refName;

            uniqueResources[key] = { ...item, refName};
        }
    }

    return Object.keys(uniqueResources).map(key => uniqueResources[key]);
}
